import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FileSpreadsheet, FileText, Landmark, Save, Sheet, Unlock, Upload } from 'lucide-react';
import { AlertBanner, Button, PageHeader, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type G50Manual,
  type G50TvaLigne,
  devaliderG50,
  downloadG50Csv,
  downloadG50Xlsx,
  getEtatFournisseurs,
  getG50Detail,
  getGabaritStatut,
  getTvaCollecteeDetail,
  getTvaDeductibleDetail,
  openEtatFournisseursPdf,
  openG50Pdf,
  saveG50Manual,
  setAmorcage,
  uploadGabarit,
  validerG50,
} from '@/services/finance';
import { openFacturePdf } from '@/services/achats';
import { openFactureVentePdf } from '@/services/ventes';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const selectCls = 'flex h-10 rounded-md border border-border bg-surface-secondary px-3 text-sm';

function Row({ label, value, strong, accent }: { label: string; value: number; strong?: boolean; accent?: string }) {
  return (
    <div className={`flex items-center justify-between gap-2 py-1.5 ${strong ? 'border-t border-border pt-2' : ''}`}>
      <span className={strong ? 'font-semibold text-content-primary' : 'text-content-secondary'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-bold' : 'font-medium'}`} style={accent ? { color: accent } : undefined}>{formatDZD(value)}</span>
    </div>
  );
}

export function FiscalitePage() {
  const qc = useQueryClient();
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const { data: d } = useQuery({ queryKey: ['g50-detail', mois, annee], queryFn: () => getG50Detail(mois, annee) });
  const { data: etat = [] } = useQuery({ queryKey: ['g50-etat', mois, annee], queryFn: () => getEtatFournisseurs(mois, annee) });

  const [manual, setManual] = useState<G50Manual>({ tap: 0, acomptes_ibs: 0, irg_salaires: 0, droit_timbre: 0 });
  const [amo, setAmo] = useState(0);
  const [openCol, setOpenCol] = useState(false);
  const [openDed, setOpenDed] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: gabarit } = useQuery({ queryKey: ['g50-gabarit'], queryFn: getGabaritStatut });
  const uploadMut = useMutation({
    mutationFn: (f: File) => uploadGabarit(f),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['g50-gabarit'] }); toast.success(r.feuilles_manquantes?.length ? `Gabarit déposé. Feuilles manquantes : ${r.feuilles_manquantes.join(', ')}.` : 'Gabarit déposé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  useEffect(() => { if (d) setManual({ tap: d.tap, acomptes_ibs: d.acomptes_ibs, irg_salaires: d.irg_salaires, droit_timbre: d.droit_timbre }); }, [d]);
  // Pré-remplit le champ d'amorçage avec le précompte antérieur courant du mois affiché.
  useEffect(() => { if (d) setAmo(d.precompte_anterieur); }, [d]);

  // Rafraîchit TOUS les mois en cache (le re-chaînage en cascade modifie les mois suivants).
  const refresh = () => qc.invalidateQueries({ queryKey: ['g50-detail'] });
  const saveMut = useMutation({ mutationFn: () => saveG50Manual(mois, annee, manual), onSuccess: () => { refresh(); toast.success('Rubriques enregistrées.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const validMut = useMutation({ mutationFn: () => validerG50(mois, annee), onSuccess: () => { refresh(); toast.success('Déclaration validée (précompte figé).'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const devalidMut = useMutation({ mutationFn: () => devaliderG50(mois, annee), onSuccess: () => { refresh(); toast.success('Déclaration repassée en brouillon (action tracée).'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const amoMut = useMutation({ mutationFn: () => setAmorcage(amo, mois, annee), onSuccess: () => { refresh(); toast.success('Amorçage enregistré.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const valide = d?.statut === 'DEPOSEE';

  return (
    <>
      <PageHeader
        title="Fiscalité — Déclaration G50"
        subtitle="À l'encaissement — agrégé exclusivement depuis le module Factures (règlements & encaissements)"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Fiscalité (G50)' }]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select value={mois} onChange={(e) => setMois(Number(e.target.value))} className={selectCls}>
              {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <Input type="number" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="h-10 w-24" />
          </div>
        }
      />

      <AlertBanner variant="info" className="mb-4">
        Le G50 <strong>n'invente rien, il agrège</strong> : TVA déductible ← règlements de l'onglet <strong>Achat</strong>,
        TVA collectée ← encaissements de l'onglet <strong>Production</strong>, rattachés par leur <strong>période de
        déclaration G50</strong>. Aucune lecture des Dépenses, bulletins ou écritures. Outil d'aide — le comptable valide et dépose.
      </AlertBanner>

      {!d ? <p className="py-10 text-center text-sm text-content-muted">Chargement…</p> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Landmark className="size-6 text-brand-primary" />
              <div>
                <p className="text-lg font-bold text-content-primary">{MOIS[mois - 1]} {annee}</p>
                <StatusBadge status={valide ? 'done' : 'working'} label={valide ? 'Validée' : 'Brouillon'} />
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase text-content-muted">Total à payer</p>
              <p className="text-2xl font-bold tabular-nums text-brand-primary">{formatDZD(d.total_a_payer)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-content-muted">TVA du mois (calculée)</h3>
              {d.collectee_par_taux.map((l) => (
                <Row key={`c${l.taux}`} label={`TVA collectée — ${Math.round(l.taux * 100)} %`} value={l.tva} />
              ))}
              <TotalExpandable label="TVA collectée (total)" value={d.tva_collectee} open={openCol} onToggle={() => setOpenCol((v) => !v)} />
              {openCol && <TvaDetailPanel mois={mois} annee={annee} sens="collectee" total={d.tva_collectee} />}
              {d.deductible_par_taux.map((l) => (
                <Row key={`d${l.taux}`} label={`TVA déductible — ${Math.round(l.taux * 100)} %`} value={l.tva} />
              ))}
              <TotalExpandable label="TVA déductible (total)" value={d.tva_deductible} open={openDed} onToggle={() => setOpenDed((v) => !v)} />
              {openDed && <TvaDetailPanel mois={mois} annee={annee} sens="deductible" total={d.tva_deductible} />}
              <Row label="Solde de TVA (collectée − déductible)" value={d.solde_tva} strong accent={d.solde_tva > 0 ? '#E2445C' : '#0F9D58'} />
            </div>

            <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-content-muted">Précompte de TVA (crédit reporté)</h3>
              <Row label={`Précompte antérieur${d.precompte_anterieur_amorcage ? ' (amorçage)' : d.precompte_anterieur_modifiable ? ' (aucun antécédent)' : ' (mois précédent)'}`} value={d.precompte_anterieur} />
              <Row label="TVA à payer" value={d.tva_a_payer} accent={d.tva_a_payer > 0 ? '#E2445C' : undefined} />
              <Row label="Précompte reporté (mois suivant)" value={d.precompte_reporte} strong accent="#0F9D58" />
              {d.precompte_anterieur_modifiable && (
                <div className="mt-3 space-y-2 border-t border-border pt-3">
                  <label className="flex flex-col text-xs text-content-muted">Précompte de départ — amorçage du 1<sup>er</sup> mois de la chaîne ({MOIS[mois - 1]} {annee})
                    <Input type="number" value={amo} onChange={(e) => setAmo(Number(e.target.value))} className="mt-1 h-9" />
                  </label>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-content-muted">
                      {d.amorcage_mois === mois && d.amorcage_annee === annee
                        ? 'Ce mois est le point de départ de la chaîne (crédit du dernier G50 avant l’ERP).'
                        : 'Définir ce mois comme point de départ de la chaîne (remplace l’amorçage actuel s’il existe).'}
                    </p>
                    <Button variant="secondary" size="sm" onClick={() => amoMut.mutate()} loading={amoMut.isPending}><Save className="size-4" /> Enregistrer l'amorçage</Button>
                  </div>
                </div>
              )}
              {d.devalide_le && (
                <p className="mt-3 border-t border-border pt-2 text-xs text-content-muted">
                  Dernière dé-validation : le {new Date(d.devalide_le).toLocaleString('fr-FR')}{d.devalide_par ? ` par ${d.devalide_par}` : ''}.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wide text-content-muted">Autres droits et taxes (saisie manuelle)</h3>
              {!valide && <Button size="sm" onClick={() => saveMut.mutate()} loading={saveMut.isPending}><Save className="size-4" /> Enregistrer</Button>}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {([['acomptes_ibs', 'Acomptes IBS'], ['irg_salaires', 'IRG sur salaires'], ['droit_timbre', 'Droit de timbre'], ['tap', 'TAP (0 par défaut)']] as const).map(([k, lbl]) => (
                <label key={k} className="flex flex-col text-xs text-content-muted">{lbl}
                  <Input type="number" disabled={valide} value={manual[k]} onChange={(e) => setManual((m) => ({ ...m, [k]: Number(e.target.value) }))} className="mt-1 h-9" />
                </label>
              ))}
            </div>
            <p className="mt-3 text-sm text-content-secondary">Total = TVA à payer + acomptes IBS + IRG salaires + droit de timbre + TAP = <strong className="tabular-nums text-brand-primary">{formatDZD(d.total_a_payer)}</strong></p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!valide
              ? <Button onClick={() => validMut.mutate()} loading={validMut.isPending}><CheckCircle2 className="size-4" /> Valider la déclaration</Button>
              : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-sm text-status-done"><CheckCircle2 className="size-4" /> Validée — précompte reporté figé pour le chaînage.</span>
                  <Button variant="secondary" onClick={() => { if (window.confirm('Dé-valider cette déclaration et la repasser en brouillon ?\n\nLa saisie (rubriques manuelles et, pour le 1er mois, l’amorçage) sera ré-ouverte, et les mois suivants seront recalculés en cascade.\n\nCette action est TRACÉE (auteur et date).')) devalidMut.mutate(); }} loading={devalidMut.isPending}><Unlock className="size-4" /> Dé-valider / repasser en brouillon</Button>
                </div>
              )}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => openG50Pdf(mois, annee).catch((e) => toast.error(getApiErrorMessage(e)))}><FileText className="size-4" /> G50 (PDF)</Button>
              <Button variant="secondary" onClick={() => openEtatFournisseursPdf(mois, annee).catch((e) => toast.error(getApiErrorMessage(e)))}><FileText className="size-4" /> État fournisseurs</Button>
              <Button variant="secondary" onClick={() => downloadG50Xlsx(mois, annee).catch((e) => toast.error((e as Error).message || 'Export impossible.'))}><Sheet className="size-4" /> G50 (Excel)</Button>
              <Button variant="secondary" onClick={() => downloadG50Csv(mois, annee).catch((e) => toast.error(getApiErrorMessage(e)))}><FileSpreadsheet className="size-4" /> CSV</Button>
            </div>
          </div>

          {/* Gabarit Excel officiel (modèle à remplir, fourni par le gérant) */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-secondary px-3 py-2 text-xs text-content-muted">
            <span>Gabarit Excel officiel :</span>
            {gabarit?.present
              ? <span className="font-medium text-status-done">✓ déposé{gabarit.modifie ? ` · ${gabarit.modifie}` : ''}</span>
              : <span className="font-medium text-status-stuck">absent — l'export Excel nécessite de déposer le modèle officiel (.xlsx)</span>}
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMut.mutate(f); e.target.value = ''; }} />
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => fileRef.current?.click()} loading={uploadMut.isPending}><Upload className="size-4" /> {gabarit?.present ? 'Remplacer le gabarit' : 'Déposer le gabarit'}</Button>
          </div>

          <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-content-muted">État des fournisseurs — tranches réglées du mois ({etat.length})</h3>
            <div className="overflow-x-auto rounded-md border border-border bg-surface-primary">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase text-content-muted">
                  <th className="px-3 py-2">Fournisseur</th><th className="px-3 py-2">NIF</th><th className="px-3 py-2">N° facture</th><th className="px-3 py-2">Date facture</th><th className="px-3 py-2">Date règlement</th><th className="px-3 py-2 text-right">Montant tranche</th><th className="px-3 py-2 text-right">TVA déduite</th>
                </tr></thead>
                <tbody>
                  {etat.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-4 text-center text-content-muted">Aucun règlement rattaché à ce mois.</td></tr>
                  ) : etat.map((x, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-1.5 font-medium text-content-primary">{x.tiers_nom || '—'}</td>
                      <td className="px-3 py-1.5 tabular-nums text-content-muted">{x.tiers_nif || '—'}</td>
                      <td className="px-3 py-1.5">{x.numero_facture || '—'}</td>
                      <td className="px-3 py-1.5 tabular-nums">{x.date_facture || '—'}</td>
                      <td className="px-3 py-1.5 tabular-nums">{x.date_reglement || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatDZD(x.montant_tranche)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(x.tva_tranche)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TotalExpandable({ label, value, open, onToggle }: { label: string; value: number; open: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-2 py-1.5 text-left hover:opacity-80">
      <span className="flex items-center gap-1 font-medium text-content-primary">
        {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}{label}
      </span>
      <span className="tabular-nums font-medium">{formatDZD(value)}</span>
    </button>
  );
}

function TvaDetailPanel({ mois, annee, sens, total }: { mois: number; annee: number; sens: 'deductible' | 'collectee'; total: number }) {
  const { data: lignes = [], isLoading } = useQuery({
    queryKey: ['g50-tva', sens, mois, annee],
    queryFn: () => (sens === 'deductible' ? getTvaDeductibleDetail(mois, annee) : getTvaCollecteeDetail(mois, annee)),
  });
  // L'API sérialise les Decimals en CHAÎNES (« 1900000.00 ») : on coerce en nombre avant
  // de sommer, sinon `0 + "1900000.00" + …` CONCATÈNE (→ NaN affiché « 0,00 »). Le collecté
  // semblait fonctionner uniquement parce qu'avec une seule ligne la soustraction finale
  // recoerçait ; avec plusieurs lignes (déductible) la concaténation cassait la réconciliation.
  const sommeTva = lignes.reduce((s, l) => s + Number(l.tva_tranche), 0);
  const ecart = Math.round((sommeTva - Number(total)) * 100) / 100;
  const ok = Math.abs(ecart) <= 1; // tolérance d'arrondi : ≤ 1 DZD → réconcilié
  const openFacture = (l: G50TvaLigne) =>
    (l.sens === 'VENTE' ? openFactureVentePdf(l.facture_id) : openFacturePdf(l.facture_id)).catch((e) => toast.error(getApiErrorMessage(e)));

  return (
    <div className="mb-2 ml-5 rounded-md border border-border bg-surface-primary p-2">
      {isLoading ? <p className="py-3 text-center text-xs text-content-muted">Chargement…</p> : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border text-left uppercase text-content-muted">
                <th className="px-2 py-1">{sens === 'deductible' ? 'Fournisseur' : 'Client'}</th>
                <th className="px-2 py-1">NIF</th><th className="px-2 py-1">N° facture</th><th className="px-2 py-1">Date facture</th>
                <th className="px-2 py-1">Date tranche</th><th className="px-2 py-1">G50</th>
                <th className="px-2 py-1 text-right">Montant</th><th className="px-2 py-1 text-right">dont HT</th><th className="px-2 py-1 text-right">dont TVA</th><th className="px-2 py-1" />
              </tr></thead>
              <tbody>
                {lignes.length === 0 ? (
                  <tr><td colSpan={10} className="px-2 py-3 text-center text-content-muted">Aucune tranche rattachée à ce mois.</td></tr>
                ) : lignes.map((l, i) => (
                  <tr key={i} className="border-b border-border/50 last:border-0">
                    <td className="px-2 py-1 font-medium text-content-primary">{l.tiers_nom || '—'}</td>
                    <td className="px-2 py-1 tabular-nums text-content-muted">{l.tiers_nif || '—'}</td>
                    <td className="px-2 py-1">{l.numero_facture || '—'}</td>
                    <td className="px-2 py-1 tabular-nums">{l.date_facture || '—'}</td>
                    <td className="px-2 py-1 tabular-nums">{l.date_tranche || '—'}</td>
                    <td className="px-2 py-1 tabular-nums text-content-muted">{String(l.periode_mois).padStart(2, '0')}/{l.periode_annee}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatDZD(l.montant_tranche)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-content-muted">{formatDZD(l.ht_tranche)}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">{formatDZD(l.tva_tranche)}</td>
                    <td className="px-2 py-1 text-right"><button type="button" onClick={() => openFacture(l)} className="text-brand-primary hover:opacity-70" title="Ouvrir la facture source"><ExternalLink className="size-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 rounded px-2 py-1.5 text-xs ${ok ? 'bg-status-done/10 text-status-done' : 'bg-status-stuck/10 text-status-stuck'}`}>
            <span>{lignes.length} ligne(s) · Σ « dont TVA » = <strong className="tabular-nums">{formatDZD(sommeTva)}</strong> · total affiché = <strong className="tabular-nums">{formatDZD(total)}</strong></span>
            <span className="font-semibold">{ok ? '✓ Réconcilié' : `⚠ Écart ${formatDZD(ecart)}`}</span>
          </div>
        </>
      )}
    </div>
  );
}
